import {
  messageDisplaySummary,
  type MessageActivityEvent,
} from "@lumen/shared";
import type { DisplayMessage } from "./ChatPage";

/**
 * Compaction checkpoints are durable system rows (the API anchors
 * `latestCompactionOnly` history slicing on them), but visually a compaction
 * happened at a specific point inside the running assistant generation —
 * between tool rounds, exactly like a tool event. Fold every checkpoint into
 * its parent assistant fragment as a compaction activity event so it renders
 * inline in that fragment's timeline, at the position where it occurred,
 * instead of as a standalone message card whose placement depends on stream
 * arrival order. Checkpoints whose parent is not in the list (pagination
 * window) stay standalone.
 */
export function foldCompactionMessages(messages: DisplayMessage[]) {
  const byId = new Map(messages.map((message) => [message.id, message]));
  const foldedEvents = new Map<string, MessageActivityEvent[]>();
  const foldedIds = new Set<string>();
  for (const message of messages) {
    if (message.role !== "system") continue;
    const event = message.activityEvents?.find(
      (
        candidate,
      ): candidate is Extract<MessageActivityEvent, { type: "compaction" }> =>
        candidate.type === "compaction",
    );
    if (!event) continue;
    const parent = message.parentId ? byId.get(message.parentId) : undefined;
    if (!parent || parent.role !== "assistant") continue;
    foldedIds.add(message.id);
    const events = foldedEvents.get(parent.id) ?? [];
    events.push(event);
    foldedEvents.set(parent.id, events);
  }
  if (!foldedIds.size) return messages;
  return messages
    .filter((message) => !foldedIds.has(message.id))
    .map((message) => {
      const events = foldedEvents.get(message.id);
      if (!events) return message;
      const activityEvents = [...(message.activityEvents ?? [])];
      for (const event of events) {
        const index = activityEvents.findIndex(
          (candidate) =>
            candidate.type === "compaction" && candidate.id === event.id,
        );
        if (index >= 0) activityEvents[index] = event;
        else activityEvents.push(event);
      }
      return {
        ...message,
        activityEvents,
        displaySummary: message.displaySummary
          ? { ...message.displaySummary, hasActivity: true }
          : message.displaySummary,
      };
    });
}

/**
 * A single generation is durably split into parent→child assistant fragment
 * rows (tool-event rollovers and compaction checkpoints both re-anchor the
 * running job to a fresh row). Rendering each fragment as its own message
 * card breaks the answer into independent chunks and pushes the compaction
 * card to a fragment boundary. Merge consecutive parent→child assistant
 * rows into one visual message so the whole generation renders as a single
 * timeline: its tool calls, text, and any folded compaction events all
 * appear inline, in order, in the same card.
 *
 * Text activity events arrive as `contentRange` offsets into their own
 * fragment's content (the response budget compacts them), so every merged
 * event is resolved to literal content against the fragment it came from.
 */
export function mergeAssistantFragments(
  messages: DisplayMessage[],
  resolveParent: (message: DisplayMessage) => string | null = (message) =>
    message.parentId,
) {
  const merged: DisplayMessage[] = [];
  for (const message of messages) {
    const previous = merged.at(-1);
    if (
      message.role === "assistant" &&
      previous?.role === "assistant" &&
      resolveParent(message) === previous.id
    )
      merged[merged.length - 1] = mergeFragment(previous, message);
    else merged.push(message);
  }
  return merged;
}

function fragmentTimeline(message: DisplayMessage) {
  const events = (message.activityEvents ?? []).map((event) =>
    event.type === "text" && event.contentRange
      ? {
          ...event,
          content: message.content.slice(
            event.contentRange.start,
            event.contentRange.end,
          ),
          // The range points into this fragment's own content; after the
          // merge it would slice into the concatenated message instead.
          contentRange: undefined,
        }
      : event,
  );
  // A fragment with content but no text event (a non-streamed round kept
  // only its content) would otherwise disappear inside the merged timeline.
  if (message.content.trim() && !events.some((event) => event.type === "text"))
    events.unshift({
      id: `text-${message.id}`,
      type: "text",
      content: message.content,
    });
  // The activity timeline is the ordering authority. A truncated canonical
  // tool list cannot tell us where a missing call belonged among text events.
  // Only synthesize a legacy timeline when no activity was stored at all.
  if (!message.activityEvents?.length)
    for (const tool of message.toolEvents ?? [])
      events.push({ id: `tool-${tool.id}`, type: "tool", tool });
  return events;
}

function mergeFragment(earlier: DisplayMessage, later: DisplayMessage) {
  return {
    ...later,
    activityMessageIds: [
      ...(earlier.activityMessageIds ?? [earlier.id]),
      ...(later.activityMessageIds ?? [later.id]),
    ],
    activityDeferred: earlier.activityDeferred || later.activityDeferred,
    displaySummary: {
      ...messageDisplaySummary(later),
      hasActivity:
        messageDisplaySummary(earlier).hasActivity ||
        messageDisplaySummary(later).hasActivity ||
        Boolean(earlier.content.trim()),
    },
    createdAt: earlier.createdAt,
    content: [earlier.content, later.content]
      .filter((content) => content.trim())
      .join("\n\n"),
    reasoning:
      [earlier.reasoning, later.reasoning]
        .filter((reasoning) => reasoning?.trim())
        .join("\n\n") || null,
    activityEvents: [...fragmentTimeline(earlier), ...fragmentTimeline(later)],
    toolEvents: [...(earlier.toolEvents ?? []), ...(later.toolEvents ?? [])],
    subagentRuns: [
      ...(earlier.subagentRuns ?? []),
      ...(later.subagentRuns ?? []),
    ],
    error: later.error ?? earlier.error,
  };
}

/**
 * The full render-time conversation timeline: compaction checkpoints fold
 * into the fragments they interrupted, then the generation's fragments
 * merge into one visual message per turn. A fragment whose parent is a
 * folded checkpoint still chains to the checkpoint's own parent fragment,
 * so a compaction never breaks the generation apart visually.
 */
export function conversationTimeline(messages: DisplayMessage[]) {
  const byId = new Map(messages.map((message) => [message.id, message]));
  const foldable = (message: DisplayMessage) =>
    message.role === "system" &&
    (message.activityEvents ?? []).some((event) => event.type === "compaction");
  const resolveParent = (message: DisplayMessage) => {
    let parentId: string | null = message.parentId;
    const seen = new Set<string>();
    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      const parent = byId.get(parentId);
      if (!parent) return parentId;
      if (parent.role === "assistant") return parent.id;
      if (foldable(parent)) {
        parentId = parent.parentId;
        continue;
      }
      return parentId;
    }
    return parentId;
  };
  return mergeAssistantFragments(
    foldCompactionMessages(messages),
    resolveParent,
  );
}

/**
 * While a message is being edited, the edit composer replaces the send
 * composer below the transcript, so everything after the edited turn is
 * hidden until the edit is saved or cancelled. The edited message itself
 * stays visible and keeps its branch position: the timeline folds
 * compaction checkpoints into fragments and merges assistant fragments
 * first, then cuts at the timeline entry that represents the edited
 * message (an edited fragment stays part of its merged generation card).
 */
export function editFocusTimeline(
  messages: DisplayMessage[],
  editedMessageId: string | null,
) {
  const timeline = conversationTimeline(messages);
  if (!editedMessageId) return timeline;
  const index = timeline.findIndex((message) =>
    message.id === editedMessageId
      ? true
      : (message.activityMessageIds ?? []).includes(editedMessageId),
  );
  return index >= 0 ? timeline.slice(0, index + 1) : timeline;
}
